
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardActions,
  CardContent,
  Button,
  Typography,
  Container,
  Fade,
  Paper,
  Divider,
  Zoom,
} from "@mui/material";

import LogoutIcon from "@mui/icons-material/Logout";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { useNavigate } from "react-router-dom";
import FormularyIcon from "../imagenes/formlary_icon.png";
import useUser from "../hooks/useUser";

export default function CategoriaFormularios() {
  const navigate = useNavigate();
  const user = useUser();

  const [showCards, setShowCards] = useState(false);

  // IDENTIFICAR EL ÁREA DEL USUARIO
  const idArea = Number(user?.id_area);

  // ==========================================
  // VERIFICAR USUARIO
  // ==========================================
  useEffect(() => {
    if (!user?.codigo_usuario) {
      navigate("/login", { replace: true });
      return;
    }

    const timer = setTimeout(() => {
      setShowCards(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [navigate, user?.codigo_usuario]);

  // ==========================================
  // CERRAR SESIÓN
  // ==========================================
  const cerrarSesion = () => {
    sessionStorage.clear();

    if (user) {
      user.codigo_usuario = null;
      user.nombre_completo = null;
      user.id_area = null;
      user.id_usuario = null;
      user.estado_usuario = null;
    }

    navigate("/login", { replace: true });
  };

  // ==========================================
  // CONFIGURACIÓN DE CATEGORÍAS POR ÁREA
  // ==========================================
  const categorias = [
    {
      nombre: "Diario",
      ruta: "/menu-listado-categorias-diario",
      areaPermitida: 1,
    },
    {
      nombre: "Semanal",
      ruta: "/menu-listado-categorias-semanal",
      areaPermitida: 1,
    },
    {
      nombre: "Quincenal",
      ruta: "/menu-listado-categorias-quincenal",
      areaPermitida: 1,
    },
    {
      nombre: "Mensual",
      ruta: "/menu-listado-categorias-mensual",
      areaPermitida: 1,
    },
    {
      nombre: "Mant. Respuestas",
      ruta: "/mantenimiento-respuestas",
      areaPermitida: 7,
    },
  ];

  // ==========================================
  // MOSTRAR SOLO LAS OPCIONES AUTORIZADAS
  // ==========================================
  const categoriasPermitidas = categorias.filter(
    (cat) => cat.areaPermitida === idArea
  );

  // ==========================================
  // INTERFAZ PRINCIPAL
  // ==========================================
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 4,
          backgroundColor: "#fdfdfd",
        }}
      >
        {/* ENCABEZADO */}
        <Fade in={true} timeout={500}>
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                md: "row",
              },
              justifyContent: "space-between",
              alignItems: {
                xs: "center",
                md: "flex-start",
              },
              mb: 4,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  textAlign: {
                    xs: "center",
                    md: "left",
                  },
                  color: "#0d47a1",
                  mb: 1,
                  letterSpacing: 1,
                }}
              >
                Categorías de Formularios
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{
                  textAlign: {
                    xs: "center",
                    md: "left",
                  },
                  color: "#546e7a",
                  mb: 2,
                }}
              >
                {idArea === 7
                  ? "Administración de respuestas de formularios."
                  : "Elige la frecuencia que deseas consultar."}
              </Typography>

              <Box
                sx={{
                  width: {
                    xs: "60%",
                    md: "250px",
                  },
                  height: "4px",
                  backgroundColor: "#1976d2",
                  borderRadius: 2,
                  mx: {
                    xs: "auto",
                    md: 0,
                  },
                  mb: {
                    xs: 3,
                    md: 0,
                  },
                }}
              />
            </Box>

            {/* CERRAR SESIÓN */}
            <Button
              variant="contained"
              color="error"
              startIcon={<LogoutIcon />}
              sx={{
                mt: {
                  xs: 2,
                  md: 0,
                },
                height: 45,
                minWidth: {
                  xs: "100%",
                  md: "180px",
                },
                px: 3,
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "1rem",
                alignSelf: {
                  xs: "center",
                  md: "center",
                },
                boxShadow: 2,
                ":hover": {
                  backgroundColor: "#c62828",
                },
              }}
              onClick={cerrarSesion}
            >
              Cerrar Sesión
            </Button>
          </Box>
        </Fade>

        <Divider sx={{ mb: 4 }} />

        {/* TARJETAS SEGÚN ÁREA */}
        <Grid
          container
          spacing={4}
          justifyContent="center"
        >
          {categoriasPermitidas.map((cat, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={cat.nombre}
            >
              <Zoom
                in={showCards}
                timeout={500 + index * 200}
              >
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow:
                      "0 4px 20px rgba(0,0,0,0.1)",
                    background:
                      "linear-gradient(135deg, #e3f2fd, #ffffff)",
                    transition:
                      "transform 0.3s ease, box-shadow 0.3s ease",
                    ":hover": {
                      transform: "translateY(-5px)",
                      boxShadow:
                        "0 6px 24px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardContent
                    sx={{ textAlign: "center" }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        color: "#37474f",
                        mb: 1,
                      }}
                    >
                      {cat.nombre}
                    </Typography>

                    <Box
                      component="img"
                      src={FormularyIcon}
                      alt={`${cat.nombre} icon`}
                      sx={{
                        height: 120,
                        width: 150,
                        objectFit: "contain",
                        mx: "auto",
                        my: 2,
                      }}
                    />
                  </CardContent>

                  <CardActions
                    sx={{
                      justifyContent: "center",
                      pb: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      size="medium"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(cat.ruta)}
                      sx={{
                        textTransform: "none",
                        fontWeight: "bold",
                        px: 3,
                        backgroundColor: "#1976d2",
                        ":hover": {
                          backgroundColor: "#1565c0",
                        },
                      }}
                    >
                      Ver
                    </Button>
                  </CardActions>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* MENSAJE SI NO EXISTEN CATEGORÍAS */}
        {categoriasPermitidas.length === 0 && (
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mt: 3 }}
          >
            No tiene módulos disponibles para su área.
          </Typography>
        )}
      </Paper>
    </Container>
  );
}
